import { useState, useEffect, useCallback } from 'react';
import { SystemRole, type User } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { useCreateUser } from '../../hooks/useUsers';
import { GoogleAddressAutocomplete } from '../common/GoogleAddressAutocomplete';
import { PhoneInput } from '../common/PhoneInput';

interface QuickCreateUserModalProps {
  open: boolean;
  /** Seeds the name field — usually the text typed into the search box. */
  initialName?: string;
  onClose: () => void;
  onCreated: (user: User) => void;
}

interface FormState {
  name: string;
  email: string;
  systemRole: SystemRole;
  phone: string;
  address: string;
}

const emptyForm: FormState = {
  name: '',
  email: '',
  systemRole: SystemRole.CLIENT,
  phone: '',
  address: '',
};

/**
 * Compact "create a contact on the spot" dialog. Built on the shared
 * {@link EntityModal} (so it stacks safely over another modal) and collects the
 * mandatory user fields (a name, and an email or — for somebody reached by phone — a phone) plus optional role/phone/address, then
 * links back via {@link onCreated}. Reused by every user picker that allows
 * inline creation (contacts, event clients, …).
 */
export function QuickCreateUserModal({
  open,
  initialName = '',
  onClose,
  onCreated,
}: QuickCreateUserModalProps) {
  const createUser = useCreateUser();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState('');

  // Reset the form each time the modal opens, seeding the typed name.
  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, name: initialName });
      setError('');
    }
  }, [open, initialName]);

  // No email is fine for somebody reached by phone — they cannot sign in, so need a phone.
  const isValid = form.name.trim() !== '' && (form.email.trim() !== '' || form.phone.trim() !== '');
  const noEmail = form.email.trim() === '';

  const handleCreate = useCallback(() => {
    if (!isValid) return;
    setError('');
    createUser.mutate(
      {
        name: form.name.trim(),
        email: form.email.trim() || null,
        systemRole: form.systemRole,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
      },
      {
        onSuccess: (user) => onCreated(user),
        onError: (err: unknown) => {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to create contact';
          setError(message);
        },
      },
    );
  }, [isValid, form, createUser, onCreated]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="New Contact"
      size="sm"
      footer={
        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!isValid || createUser.isPending}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            data-testid="quick-create-user-submit"
          >
            {createUser.isPending ? 'Creating…' : 'Create & Link'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}

        <div>
          <label htmlFor="quick-user-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="quick-user-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Full name"
            autoFocus
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="quick-user-email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email
          </label>
          <input
            id="quick-user-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="user@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          {noEmail && (
            <p className="mt-1 text-xs text-gray-500">
              Without an email they cannot sign in — enter a phone to reach them.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="quick-user-role" className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <select
            id="quick-user-role"
            value={form.systemRole}
            onChange={(e) => setForm((f) => ({ ...f, systemRole: e.target.value as SystemRole }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
          >
            {Object.values(SystemRole).map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="quick-user-phone"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Phone
          </label>
          <PhoneInput
            id="quick-user-phone"
            value={form.phone}
            onChange={(phone) => setForm((f) => ({ ...f, phone }))}
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label
            htmlFor="quick-user-address"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Address
          </label>
          <GoogleAddressAutocomplete
            id="quick-user-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((f) => ({ ...f, address: fields.formattedAddress }))
            }
            placeholder="Optional"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
    </EntityModal>
  );
}
